import { Accordion, Stack } from '@mantine/core'
import React, { useState } from 'react'
import { ROUTE } from 'shared-constants'
import slugify from 'slugify'
import styled from 'styled-components'
import Icon from 'components/Icon'
import Link from 'components/Link'
import { MarkdownRenderer } from 'components/MarkdownRenderer'
import { Text } from 'components/Typography'
import { PURCHASE_TERMS_SLUG, RETURN_POLICY_SLUG } from 'constants/common'
import { SPACE, FONT_WEIGHT, FONT_SIZE, COLOR_INTENT, FONT_STACK } from 'Theme'
import { useMedia } from 'useMedia'
import { formatDimensionsText } from '../../utils'
import { ProductCustom } from '../ProductCustom'
import { Proposition65WarningModal } from '../Proposition65WarningModal'
import { VariantAccordion } from './VariantAccordion'

const AccordionHeaderText = ({
  children,
  ...rest
}: React.ComponentProps<typeof Text>) => (
  <Text
    color={COLOR_INTENT.VARIANT_PICKER.GROUP_COLOR}
    fontSize={FONT_SIZE.PX_13}
    fontWeight={FONT_WEIGHT.MEDIUM}
    letterSpacing="0.1em"
    {...rest}
  >
    {children}
  </Text>
)

type FormatDimensionsTextParams = Parameters<typeof formatDimensionsText>[0]

export interface VariantDetailAccordionProps
  extends React.ComponentProps<typeof ProductCustom>,
    FormatDimensionsTextParams {
  brand?: string
  brandSlug?: string
  detailsRef: React.RefObject<HTMLDivElement>
  accordionDetails?: string
  customDimensions?: string
  isEligibleForProposition65?: boolean
  proposition65?: string
  accordionShipping?: string
  accordionCare?: string
}

const TextButton = styled(Text)`
  &:hover {
    cursor: pointer;
  }
`

export const VariantDetails: React.FC<
  React.PWC<VariantDetailAccordionProps>
> = ({
  brand,
  detailsRef,
  accordionCare,
  accordionDetails,
  accordionShipping,
  customDimensions,
  isEligibleForProposition65,
  proposition65,
  customProductDescription,
  customProductHeadline,
  depth,
  height,
  length,
  width,
  dimensionUnit,
}) => {
  const [value, setValue] = useState<string | null | string[]>([])
  const [isProposition65ModalOpen, setIsProposition65ModalOpen] =
    useState(false)
  const { MOBILE: isMobile } = useMedia()

  return (
    <>
      {(accordionDetails || isEligibleForProposition65) && (
        <Stack gap={SPACE.PX_6} my={SPACE.PX_22} ref={detailsRef}>
          <AccordionHeaderText>ABOUT THIS PIECE</AccordionHeaderText>
          <MarkdownRenderer
            markdownSource={accordionDetails}
            fontSize={FONT_SIZE.PX_14}
            fontFamily={FONT_STACK.SANS}
          />
          {isEligibleForProposition65 && (
            <TextButton
              color={COLOR_INTENT.PRODUCT_ACCORDION.PROPOSITION_65_BUTTON}
              onClick={() => setIsProposition65ModalOpen(true)}
              mt={SPACE.PX_20}
              fontSize={FONT_SIZE.PX_16}
            >
              WARNING For California Residents
            </TextButton>
          )}
        </Stack>
      )}
      {isEligibleForProposition65 && (
        <Proposition65WarningModal
          isOpen={isProposition65ModalOpen}
          onModalClose={() => setIsProposition65ModalOpen(false)}
          chemical={proposition65}
        />
      )}
      <VariantAccordion
        value={value}
        onChange={setValue}
        showTopBorder
        multiple
      >
        <Accordion.Item value="dimensions">
          <Accordion.Control
            chevron={
              value.includes('dimensions') ? <Icon.Minus /> : <Icon.Plus />
            }
          >
            <AccordionHeaderText>DIMENSIONS</AccordionHeaderText>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack>
              {formatDimensionsText({
                depth,
                height,
                length,
                width,
                dimensionUnit,
              }).map(({ key, value: dimension }) => (
                <Text
                  key={key}
                  fontWeight={FONT_WEIGHT.MEDIUM}
                  fontSize={FONT_SIZE.PX_14}
                >
                  {`${key}: `}
                  <Text fontSize={FONT_SIZE.PX_14}>{dimension}</Text>
                </Text>
              ))}
              {customDimensions && (
                <Text
                  fontWeight={FONT_WEIGHT.MEDIUM}
                  fontSize={FONT_SIZE.PX_14}
                >
                  Other:{' '}
                  <Text fontSize={FONT_SIZE.PX_14}>{customDimensions}</Text>
                </Text>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {brand && (
          <Accordion.Item value="brand">
            <Accordion.Control
              chevron={value.includes('brand') ? <Icon.Minus /> : <Icon.Plus />}
            >
              <AccordionHeaderText>BRAND</AccordionHeaderText>
            </Accordion.Control>
            <Accordion.Panel>
              <Link
                mb={SPACE.PX_4}
                fontWeight={FONT_WEIGHT.MEDIUM}
                fontSize={FONT_SIZE.PX_14}
                // `/` is needed for ensuring the link is not used as relative path
                // href={`/${brand?.cmsBrandPage.cmsPage.slug}`}
                href={ROUTE.SHOWROOM_BRAND_DETAIL({
                  slug: slugify(brand, { lower: true, strict: true }),
                })}
              >
                {brand}
              </Link>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/*
          Drawn on every product. It used to appear only where the product
          carried `accordionShipping`, and a brand synced from a feed rather
          than through the merch tooling never gets that field — the mapper
          does not emit it and the admin input that would write it is disabled
          — so those PDPs showed no shipping or returns terms at all
          (HRNS-4042).

          The fallback links to the Return Policy and the Purchase Terms the
          site already serves at checkout and in the footer, rather than
          restating them here where the second copy would drift from the first.
        */}
        <Accordion.Item value="shipping">
          <Accordion.Control
            chevron={
              value.includes('shipping') ? <Icon.Minus /> : <Icon.Plus />
            }
          >
            <AccordionHeaderText>SHIPPING & RETURNS</AccordionHeaderText>
          </Accordion.Control>
          <Accordion.Panel>
            {accordionShipping ? (
              <MarkdownRenderer
                markdownSource={accordionShipping}
                fontSize={FONT_SIZE.PX_14}
                fontFamily={FONT_STACK.SANS}
              />
            ) : (
              <Stack gap={SPACE.PX_6}>
                <Text fontSize={FONT_SIZE.PX_14}>
                  Shipping is calculated at checkout, where the options and cost
                  for your address are shown before you pay.
                </Text>
                <Text fontSize={FONT_SIZE.PX_14}>
                  See our{' '}
                  <Link
                    fontSize={FONT_SIZE.PX_14}
                    href={ROUTE.BLOG_DETAIL({ slug: RETURN_POLICY_SLUG })}
                  >
                    Return Policy
                  </Link>{' '}
                  and{' '}
                  <Link
                    fontSize={FONT_SIZE.PX_14}
                    href={ROUTE.BLOG_DETAIL({ slug: PURCHASE_TERMS_SLUG })}
                  >
                    Purchase Terms and Conditions
                  </Link>
                  .
                </Text>
              </Stack>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        {accordionCare && (
          <Accordion.Item value="care">
            <Accordion.Control
              chevron={value.includes('care') ? <Icon.Minus /> : <Icon.Plus />}
            >
              <AccordionHeaderText>CARE</AccordionHeaderText>
            </Accordion.Control>
            <Accordion.Panel>
              <MarkdownRenderer
                markdownSource={accordionCare}
                fontSize={FONT_SIZE.PX_14}
                fontFamily={FONT_STACK.SANS}
              />
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </VariantAccordion>
      {!isMobile && (
        <ProductCustom
          customProductDescription={customProductDescription}
          customProductHeadline={customProductHeadline}
        />
      )}
    </>
  )
}
